import React from "react";
import _ from "lodash";
import Select from "react-select";
import ReactTooltip from "react-tooltip";
import numeral from "numeral";
import ModalWrapper from "components/common/ModalWrapper";
import Checkbox from "components/common/Checkbox";
import LoadingUI from "components/common/LoadingUI";
import UnitFullPath from "components/common/UnitFullPath";
import StickyTableWrapper from "components/common/StickyTableWrapper";

import {
  tdClass,
  thClass,
  inputClass,
  buttonModalConfirmClass,
  buttonModalCancelClass,
  buttonModalConfirmDisable,
} from "helpers/StyleClass";
import { REQUEST_TYPES } from "constants/RequestType";
import { MESSAGE } from "constants/Message";

import { OnboardingAPI, NetworkAPI } from "apis";

import { FiInfo } from "react-icons/fi";

const METRIC = {
  BILLABLE_REVENUE: "BILLABLE_REVENUE",
  HOLISTIC_REVENUE: "HOLISTIC_REVENUE",
  GOOGLE_REVENUE: "GOOGLE_REVENUE",
};

const BILLABLE_REQUEST_TYPES = [
  REQUEST_TYPES.GPT,
  REQUEST_TYPES.AMP,
  REQUEST_TYPES.MOBILE_SDK,
];

const REQUEST_TYPE_COLOR = {
  [REQUEST_TYPES.GPT]: "bg-blue-200",
  [REQUEST_TYPES.AMP]: "bg-green-200",
  [REQUEST_TYPES.MOBILE_SDK]: "bg-orange-200",
};

const TYPE = {
  RATIO: "RATIO",
  VOLUME: "VOLUME",
};

const TYPE_TOOLTIP = {
  RATIO: "unit’s value / whole network’s value",
  VOLUME: "unit’s value",
};

class RecommendUnitsModal extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isLoading: false,
      msg: [],

      isCST: false,
      requestTypeOptions: _.map(BILLABLE_REQUEST_TYPES, (rt) => ({
        value: rt,
        label: rt,
      })),

      requestTypes: [],
      parentPath: "",
      name: "",
      threshold: 0.01,
      metric: METRIC.BILLABLE_REVENUE,
      type: TYPE.RATIO,
      billingKeyTaggingRequired: false,

      data: null,
      selected: [],
      dataType: null,
      dataMetric: null,
    };

    this.handleGetRecommend = this.handleGetRecommend.bind(this);
    this.handleSelectAllChange = this.handleSelectAllChange.bind(this);
    this.handleSelectChange = this.handleSelectChange.bind(this);
    this.handleImport = this.handleImport.bind(this);
  }

  async componentDidMount() {
    const { network } = this.props;
    this.setState({ isLoading: true, msg: [] });

    try {
      const result = await NetworkAPI.getNetworkInfo({
        networkId: network.networkId,
      });
      this.setState({ isCST: result.product !== 1 });
    } catch (e) {
      this.setState({ msg: [e, MESSAGE.ERROR] });
    }

    this.setState({ isLoading: false });
  }

  async handleGetRecommend() {
    const { network } = this.props;
    const {
      requestTypes,
      parentPath,
      name,
      threshold,
      metric,
      type,
      billingKeyTaggingRequired,
    } = this.state;

    const params = {
      networkId: network.networkId,
      threshold: _.toNumber(threshold),
      metric,
      type,
    };

    if (requestTypes.length > 0) {
      params.requestTypes = requestTypes;
    }

    if (parentPath !== "") {
      params.parentPath = parentPath;
    }

    if (name !== "") {
      params.name = name;
    }

    if (billingKeyTaggingRequired) {
      params.billingKeyTaggingRequired = billingKeyTaggingRequired;
    }

    this.setState({ isLoading: true, msg: [] });
    try {
      const result = await OnboardingAPI.getRecommendedAdUnitCandidates(params);
      this.setState({
        data: result,
        dataType: type,
        dataMetric: metric,
        selected: _.map(result, (d) => d.extGamUnitId),
      });
    } catch (e) {
      this.setState({ msg: [e, MESSAGE.ERROR] });
    }

    this.setState({ isLoading: false });
  }

  handleSelectAllChange(flag) {
    const { data } = this.state;
    this.setState({
      selected: flag ? _.map(data, (d) => d.extGamUnitId) : [],
    });
  }

  handleSelectChange(id) {
    const { selected } = this.state;
    this.setState({
      selected: selected.includes(id)
        ? _.filter(selected, (s) => s !== id)
        : [...selected, id],
    });
  }

  handleImport() {
    const { selected } = this.state;
    this.props.handleConfirm(selected);
  }

  render() {
    const { network } = this.props;
    const {
      isLoading,
      msg,
      isCST,
      requestTypeOptions,
      requestTypes,
      parentPath,
      name,
      threshold,
      metric,
      type,
      billingKeyTaggingRequired,
      data,
      selected,
      dataType,
      dataMetric,
    } = this.state;

    const parsedRequestTypes = requestTypes.map((rt) =>
      _.find(requestTypeOptions, (opt) => opt.value === rt)
    );

    const parsedSelected = selected
      .map((id) => _.find(data || [], (d) => d.extGamUnitId === id))
      .filter((d) => d != null);

    const sumSelectedValue = _.sumBy(parsedSelected, (d) => d.value);

    const detailList = parsedSelected.flatMap((d) => d.valueDetails);
    const groupList = _.groupBy(detailList, (detail) => detail.requestType);
    const sumSelectedByType = _.mapValues(groupList, (details) =>
      _.sumBy(details, (d) => d.value)
    );

    return (
      <>
        <ModalWrapper
          isOpen={true}
          showCloseFooter={false}
          width="75%"
          title={{
            label: "Recommend Units for network ",
            highlight: `${network.networkId} - ${network.name}`,
          }}
        >
          {isLoading && (
            <div className="absolute left-0 top-0 z-50 flex h-full w-full items-center justify-center bg-gray-800 opacity-25">
              <LoadingUI iconOnly={true}></LoadingUI>
            </div>
          )}
          <div className="flex gap-4">
            <div className="w-1/3">
              <div className="font-semibold text-gray-600">Filter</div>
              <div className="border rounded mb-4 flex flex-col gap-2 border-gray-400 p-2">
                <div>
                  <label className="mt-1">Request Type</label>
                  <Select
                    value={parsedRequestTypes}
                    onChange={(selected) =>
                      this.setState({
                        requestTypes: selected
                          ? selected.map((s) => s.value)
                          : [],
                      })
                    }
                    options={requestTypeOptions}
                    isMulti={true}
                  ></Select>
                </div>

                <div>
                  <label>Parent path</label>
                  <input
                    className={inputClass}
                    type="text"
                    value={parentPath}
                    onChange={(e) => {
                      this.setState({ parentPath: e.target.value });
                    }}
                  ></input>
                </div>

                <div>
                  <label>Name</label>
                  <input
                    className={inputClass}
                    type="text"
                    value={name}
                    onChange={(e) => {
                      this.setState({ name: e.target.value });
                    }}
                  ></input>
                </div>
              </div>

              <div className="font-semibold text-gray-600">Sort by</div>
              <div className="border rounded mb-4 flex flex-col gap-2 border-gray-400 p-2">
                <div>
                  <label>Metric</label>
                  <div className="flex flex-wrap gap-2">
                    {_.values(METRIC).map((m) => (
                      <label
                        htmlFor={`radio_metric_${m}`}
                        className="flex cursor-pointer items-center gap-1"
                        key={m}
                      >
                        <input
                          id={`radio_metric_${m}`}
                          type="radio"
                          checked={metric === m}
                          onChange={() => this.setState({ metric: m })}
                          className="h-4"
                        />{" "}
                        {_.startCase(m.toLowerCase())}
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-1">
                    Type
                    <span
                      data-tip
                      data-for="tooltip_type"
                      className="text-sm text-blue-400"
                    >
                      <FiInfo></FiInfo>
                    </span>
                    <ReactTooltip
                      id="tooltip_type"
                      type="dark"
                      place="right"
                      getContent={() => {
                        return (
                          <>
                            <div className="text-left">
                              Ratio: {TYPE_TOOLTIP.RATIO}
                            </div>
                            <div className="text-left">
                              Volume: {TYPE_TOOLTIP.VOLUME}
                            </div>
                          </>
                        );
                      }}
                    ></ReactTooltip>
                  </div>
                  <div className="flex gap-4">
                    {_.values(TYPE).map((t) => (
                      <label
                        htmlFor={`radio_type_${t}`}
                        className="flex cursor-pointer items-center gap-1"
                        key={t}
                      >
                        <input
                          id={`radio_type_${t}`}
                          type="radio"
                          checked={type === t}
                          onChange={() => this.setState({ type: t })}
                        />{" "}
                        {_.startCase(t.toLowerCase())}
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-1">
                    Threshold
                    <span
                      data-tip
                      data-for="tooltip_threshold"
                      className="text-sm text-blue-400"
                    >
                      <FiInfo></FiInfo>
                    </span>
                    <ReactTooltip
                      id="tooltip_threshold"
                      type="dark"
                      place="right"
                      getContent={() => {
                        return (
                          <>
                            <div className="text-left">
                              Ratio: eg. 0.01 (This unit’s billable revenue
                              ratio needs to be {">"}
                              1%)
                            </div>
                            <div className="text-left">
                              Volume: eg. 100 (This unit’s google revenue needs
                              to be {">"} $100)
                            </div>
                          </>
                        );
                      }}
                    ></ReactTooltip>
                  </div>
                  <input
                    className={inputClass}
                    type="number"
                    value={threshold}
                    onChange={(e) => {
                      this.setState({ threshold: e.target.value });
                    }}
                  ></input>
                </div>
              </div>

              {isCST && (
                <div className="rounded mb-4 bg-blue-200 p-2">
                  <label className="flex cursor-pointer items-center">
                    <Checkbox
                      isChecked={billingKeyTaggingRequired}
                      onChange={() =>
                        this.setState({
                          billingKeyTaggingRequired: !billingKeyTaggingRequired,
                        })
                      }
                    ></Checkbox>
                    <span className=" ml-1 align-middle text-sm font-semibold text-gray-700 hover:text-gray-700">
                      Billing key is attached to request
                    </span>
                  </label>
                </div>
              )}

              <div className="flex justify-end">
                <button
                  className={buttonModalConfirmClass}
                  onClick={this.handleGetRecommend}
                >
                  Get Recommendations
                </button>
              </div>
            </div>
            <div className="flex w-2/3 flex-col">
              <div className="font-semibold text-gray-600">
                Recommend Units {data && `(${data.length})`}
              </div>
              <div
                className="border rounded border-gray-400 bg-gray-100 text-sm"
                style={{ height: "32rem" }}
              >
                {data && (
                  <StickyTableWrapper maxHeight="100%">
                    <table className="w-full table-fixed">
                      <thead>
                        <tr>
                          <th className={`${thClass} w-12 bg-gray-100`}>
                            <Checkbox
                              isChecked={selected.length === data.length}
                              onChange={this.handleSelectAllChange}
                            ></Checkbox>
                          </th>
                          <th
                            className={`${thClass} bg-gray-100`}
                            style={{ minWidth: "18rem" }}
                          >
                            Unit
                          </th>
                          <th
                            className={`${thClass} bg-gray-100`}
                            style={{ width: "12rem" }}
                          >
                            Reqeust Type
                          </th>
                          <th className={`${thClass} w-32 bg-gray-100`}>
                            {_.startCase(dataMetric)} - {_.startCase(dataType)}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.length > 0 ? (
                          _.map(data, (d) => (
                            <tr key={d.extGamUnitId}>
                              <td className={tdClass + " -border-l"}>
                                <Checkbox
                                  isChecked={selected.includes(d.extGamUnitId)}
                                  onChange={() =>
                                    this.handleSelectChange(d.extGamUnitId)
                                  }
                                ></Checkbox>
                              </td>
                              <td className={tdClass}>
                                [{d.extGamUnitId}]{" "}
                                <UnitFullPath
                                  path={d.parentPath}
                                  name={d.name}
                                  replaceClass="break-all text-xs"
                                ></UnitFullPath>
                              </td>
                              <td className={tdClass}>
                                <div className="flex flex-wrap gap-2 text-xs">
                                  {_.map(d.valueDetails, (detail, idx) => (
                                    <RequestTypeLabel
                                      key={`${detail.requestType}-${idx}`}
                                      requestType={detail.requestType}
                                      value={detail.value}
                                    ></RequestTypeLabel>
                                  ))}
                                </div>
                              </td>
                              <td className={tdClass}>
                                {dataType === TYPE.RATIO
                                  ? `${numeral(d.value * 100).format(
                                      "0,0.00"
                                    )}%`
                                  : numeral(d.value).format("$0,0.00")}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan="5"
                              className={`${tdClass} text-center font-semibold text-gray-600`}
                            >
                              No data
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </StickyTableWrapper>
                )}
              </div>
              {data && (
                <div className="flex items-center justify-end bg-gray-200 text-xs">
                  <div
                    className={`flex flex-wrap gap-2 ${tdClass} overflow-y-auto ${
                      _.keys(sumSelectedByType).length > 1 &&
                      "flex-1 justify-end"
                    }`}
                    style={{
                      width:
                        _.keys(sumSelectedByType).length > 1 ? "auto" : "12rem",
                      minHeight: "2.875rem",
                      maxHeight: "4rem",
                    }}
                  >
                    {_.map(_.keys(sumSelectedByType), (key) => (
                      <RequestTypeLabel
                        key={key}
                        requestType={key}
                        value={sumSelectedByType[key]}
                      ></RequestTypeLabel>
                    ))}
                  </div>
                  <div className={`${tdClass} w-32 font-semibold`}>
                    {dataType === TYPE.RATIO
                      ? `${numeral(sumSelectedValue).format("0.00%")}`
                      : numeral(sumSelectedValue).format("$0,0.00")}
                  </div>
                </div>
              )}
              <div className="mt-2 flex justify-end gap-2">
                <button
                  className={buttonModalCancelClass}
                  onClick={() => this.props.handleCancel()}
                >
                  Cancel
                </button>
                <button
                  className={
                    selected.length > 0
                      ? buttonModalConfirmClass
                      : buttonModalConfirmDisable
                  }
                  disabled={selected.length === 0}
                  onClick={this.handleImport}
                >
                  Import selected units ({selected.length})
                </button>
              </div>
            </div>
          </div>

          <div className="mt-1 h-10 w-full overflow-y-auto">
            {msg && msg.length > 0 && (
              <div
                className={`${
                  msg[1] === MESSAGE.ERROR ? "text-red-500" : "text-green-500"
                }`}
              >
                {msg[0]}
              </div>
            )}
          </div>
        </ModalWrapper>
      </>
    );
  }
}

function RequestTypeLabel({ requestType, value }) {
  return (
    <div className={`px-2 py-1 ${REQUEST_TYPE_COLOR[requestType]}`}>
      <div className="text-black">{requestType}</div>
      <div className="font-semibold">{numeral(value).format("$0,0.00")}</div>
    </div>
  );
}

export default RecommendUnitsModal;
